/* ========== SERIES PARSER ==========
 * Reads everything inside a single <c:ser> node:
 *   - series name (from c:tx)
 *   - data values (c:val or c:yVal numCache)
 *   - x values for scatter/bubble (c:xVal)
 *   - bubble sizes for bubble (c:bubbleSize)
 *   - series-level color (c:spPr)
 *   - per-data-point colors (c:dPt > c:spPr)
 *
 * Honors `pt.idx` so missing points don't shift later values.
 * ==================================== */

import { qLocal, qLocalAll, qDirectChild } from "../xmlUtils";
import { resolveSpPrColor } from "./colorResolver";

/**
 * Read a numCache (or fallback container) into a dense, idx-aware array.
 * Missing indices are filled with 0 so the renderer can iterate cleanly.
 */
function readIndexedNumeric(cacheNode) {
  if (!cacheNode) return [];
  const ptNodes = qLocalAll(cacheNode, "pt");
  const ptCountNode = qLocal(cacheNode, "ptCount");
  const expectedCount = ptCountNode ? parseInt(ptCountNode.getAttribute("val") || "0", 10) : 0;
  const indexed = [];
  let maxIdx = -1;
  ptNodes.forEach(pt => {
    const idx = parseInt(pt.getAttribute("idx") || "0", 10);
    const v = qLocal(pt, "v");
    const num = v ? parseFloat(v.textContent) : NaN;
    indexed[idx] = isNaN(num) ? 0 : num;
    if (idx > maxIdx) maxIdx = idx;
  });
  const total = Math.max(expectedCount, maxIdx + 1);
  const out = [];
  for (let i = 0; i < total; i++) out.push(indexed[i] != null ? indexed[i] : 0);
  return out;
}

/** Extract a series's display name from its <c:tx> node. */
function readSeriesName(ser) {
  let name = "";
  const txNode = qLocal(ser, "tx");
  if (txNode) {
    const strRef = qLocal(txNode, "strRef");
    if (strRef) {
      const strCache = qLocal(strRef, "strCache");
      const vNode = strCache ? qLocal(strCache, "v") : null;
      if (vNode) name = vNode.textContent.trim();
      if (!name) {
        const fNode = qLocal(strRef, "f");
        if (fNode) name = fNode.textContent.trim().replace(/^.*!/, "").replace(/\$/g, "");
      }
    }
    if (!name) {
      const directV = qDirectChild(txNode, "v");
      if (directV) name = directV.textContent.trim();
    }
    if (!name) {
      const richNode = qLocal(txNode, "rich");
      if (richNode) {
        const txTexts = qLocalAll(richNode, "t");
        if (txTexts.length > 0) name = txTexts.map(t => t.textContent).join("").trim();
      }
    }
  }
  if (!name) {
    const orderNode = qLocal(ser, "order");
    const orderVal = orderNode ? orderNode.getAttribute("val") : null;
    name = "Series" + (orderVal != null ? parseInt(orderVal, 10) + 1 : "");
  }
  return name;
}

/** Read per-point colors from <c:dPt> overrides. */
function readPointColors(ser) {
  const dPtNodes = qLocalAll(ser, "dPt");
  const pointColors = [];
  dPtNodes.forEach(dPt => {
    const idxNode = qDirectChild(dPt, "idx");
    const idx = idxNode ? parseInt(idxNode.getAttribute("val") || "0", 10) : 0;
    const dPtSpPr = qDirectChild(dPt, "spPr");
    const c = resolveSpPrColor(dPtSpPr);
    if (c) pointColors[idx] = c;
  });
  return pointColors;
}

/**
 * Read per-point explosion offsets from <c:dPt><c:explosion val="N"/>.
 * The value is a percentage (0-400) representing slice offset relative
 * to the pie radius — 25 = pulled 25% of radius outward.
 */
function readPointExplosions(ser) {
  const dPtNodes = qLocalAll(ser, "dPt");
  const explosions = [];
  dPtNodes.forEach(dPt => {
    const idxNode = qDirectChild(dPt, "idx");
    const idx = idxNode ? parseInt(idxNode.getAttribute("val") || "0", 10) : 0;
    const expNode = qDirectChild(dPt, "explosion");
    if (expNode) {
      const v = parseInt(expNode.getAttribute("val") || "0", 10);
      if (v > 0) explosions[idx] = v;
    }
  });
  return explosions;
}

/**
 * Parse a single <c:ser> node into a renderer-friendly series object.
 * Returns null if the series has no values (empty data shouldn't render).
 */
export function parseSeries(ser, renderType, barDir) {
  // Values
  const valNode = qLocal(ser, "val") || qLocal(ser, "yVal");
  const numCache = valNode ? (qLocal(valNode, "numCache") || valNode) : null;
  const vals = readIndexedNumeric(numCache);

  // The source range formula (e.g. "Sheet1!$B$2:$B$13") — kept so the viewer
  // can re-resolve the series against LIVE sheet data after cell edits,
  // instead of only ever showing the file's cached values.
  const valFNode = valNode ? qLocal(valNode, "f") : null;
  const valuesRef = valFNode ? valFNode.textContent.trim() : null;
  // No cached points AND no source range -> nothing to plot. (A series with
  // a range but no cache — written by some tools — is kept; its values are
  // read live from the sheet by utils/chartLiveData.)
  if (vals.length === 0 && !valuesRef) return null;

  // Category source range (labels), re-read live the same way.
  const catNode = qLocal(ser, "cat");
  const catFNode = catNode ? qLocal(catNode, "f") : null;
  const categoriesRef = catFNode ? catFNode.textContent.trim() : null;

  // X values (scatter/bubble)
  let xVals = null;
  const xValNode = qLocal(ser, "xVal");
  if (xValNode) {
    const xCache = qLocal(xValNode, "numCache") || qLocal(xValNode, "strCache") || xValNode;
    xVals = readIndexedNumeric(xCache);
  }

  // Bubble sizes
  let bubbleSizes = null;
  const bubbleSizeNode = qLocal(ser, "bubbleSize");
  if (bubbleSizeNode) {
    const bCache = qLocal(bubbleSizeNode, "numCache") || bubbleSizeNode;
    bubbleSizes = readIndexedNumeric(bCache);
  }

  // Colors
  const seriesColor = resolveSpPrColor(qDirectChild(ser, "spPr"));
  const pointColors = readPointColors(ser);
  const pointExplosions = readPointExplosions(ser);

  // For bar charts, distinguish horizontal "bar" from vertical "col" so the
  // renderer can pick the right drawer per-series in combo charts.
  let serRenderType = renderType;
  if (renderType === "bar") {
    serRenderType = barDir === "col" ? "col" : "bar";
  }

  const seriesObj = {
    name: readSeriesName(ser),
    data: vals,
    renderType: serRenderType,
  };
  if (valuesRef) seriesObj.valuesRef = valuesRef;
  if (categoriesRef) seriesObj.categoriesRef = categoriesRef;
  if (seriesColor) seriesObj.color = seriesColor;
  if (pointColors.length > 0) seriesObj.pointColors = pointColors;
  if (pointExplosions.length > 0) seriesObj.pointExplosions = pointExplosions;
  if (xVals && xVals.length > 0) seriesObj.xData = xVals;
  if (bubbleSizes && bubbleSizes.length > 0) seriesObj.bubbleSizes = bubbleSizes;
  return seriesObj;
}

/** Read pts from a strCache/numCache, honoring pt.idx. */
function readCachePts(cache) {
  const pts = qLocalAll(cache, "pt");
  const ptCountNode = qLocal(cache, "ptCount");
  const expected = ptCountNode ? parseInt(ptCountNode.getAttribute("val") || "0", 10) : 0;
  const indexed = [];
  let maxI = -1;
  pts.forEach(pt => {
    const idx = parseInt(pt.getAttribute("idx") || "0", 10);
    const v = qLocal(pt, "v");
    indexed[idx] = v ? v.textContent : "";
    if (idx > maxI) maxI = idx;
  });
  return { indexed, count: Math.max(expected, maxI + 1) };
}

/**
 * Read multi-level categories — Excel writes hierarchical category labels
 * as <c:multiLvlStrCache> with one <c:lvl> per axis level. Level 0 is the
 * most detailed (e.g. day), higher levels are parents (year, month).
 * Excel displays them joined from highest level to lowest, e.g.
 * "Jan 2020 Sunday".
 */
function readMultiLevelCats(multiLvlCache) {
  const lvlNodes = qLocalAll(multiLvlCache, "lvl");
  if (lvlNodes.length === 0) return [];

  const lvls = lvlNodes.map(lvl => readCachePts(lvl));
  const total = Math.max.apply(null, lvls.map(l => l.count).concat([0]));

  const cats = [];
  for (let i = 0; i < total; i++) {
    // Join highest level first, lowest last — matches Excel's label order.
    const parts = [];
    for (let l = lvls.length - 1; l >= 0; l--) {
      const val = lvls[l].indexed[i];
      if (val) parts.push(val);
    }
    cats.push(parts.join(" "));
  }
  return cats;
}

/**
 * Read multi-level categories as SEPARATE levels for axis rendering.
 * Excel displays them as stacked rows under the axis: level 0 (the most
 * detailed, e.g. "Sunday") closest to the axis, parents ("2020", "Jan")
 * on the rows below. Returns [[lvl0…], [lvl1…], …] or null when the
 * chart has no multi-level categories.
 */
export function readCategoryLevels(serNodes) {
  for (const ser of serNodes) {
    const catNode = qLocal(ser, "cat");
    if (!catNode) continue;
    const multiLvlCache = qLocal(catNode, "multiLvlStrCache");
    if (!multiLvlCache) return null;
    const lvlNodes = qLocalAll(multiLvlCache, "lvl");
    if (lvlNodes.length < 2) return null;
    const lvls = lvlNodes.map(lvl => readCachePts(lvl));
    const total = Math.max.apply(null, lvls.map(l => l.count).concat([0]));
    return lvls.map(l => {
      const out = [];
      for (let i = 0; i < total; i++) out.push(l.indexed[i] != null ? l.indexed[i] : "");
      return out;
    });
  }
  return null;
}

/**
 * Read shared category labels off the first series that has them.
 * Honors pt.idx and supports both single-level (strCache/numCache) and
 * multi-level (multiLvlStrCache) categories.
 */
export function readCategories(serNodes) {
  for (const ser of serNodes) {
    const catNode = qLocal(ser, "cat");
    if (!catNode) continue;

    // 1. Multi-level categories first — Excel's Pie of Pie / Doughnut /
    //    grouped axes all use this when the source range spans multiple
    //    columns of labels.
    const multiLvlCache = qLocal(catNode, "multiLvlStrCache");
    if (multiLvlCache) {
      const cats = readMultiLevelCats(multiLvlCache);
      if (cats.length > 0) return cats;
    }

    // 2. Single-level categories
    const cache = qLocal(catNode, "strCache") || qLocal(catNode, "numCache");
    if (!cache) continue;
    const { indexed, count } = readCachePts(cache);
    const cats = [];
    for (let i = 0; i < count; i++) cats.push(indexed[i] != null ? indexed[i] : "");
    if (cats.length > 0) return cats;
  }
  return [];
}
